import { useMemo, useState, useRef, useEffect } from "react";
import {
  FolderKanban,
  Search,
  Scissors,
  Stethoscope,
  ChevronRight,
  ChevronLeft,
  X,
  Layers,
  Activity,
  User,
  CalendarDays,
  MapPin,
  ClipboardList,
  Lightbulb,
  Image as ImageIcon,
  AlertOctagon,
  BookOpen,
  ArrowUpRight,
  Waves,
} from "lucide-react";
import { GlassCard } from "@/components/system/GlassCard";
import ProvenanceTag from "@/components/system/ProvenanceTag";
import {
  getCases,
  getPerson,
  getEncountersByCaseId,
  getTimelineByCaseId,
  getReflectionsByCaseId,
  getOperativePearlsByCaseId,
  getImagingByCaseId,
  getMMByCaseId,
  getSurgicalDetailByCaseId,
  getPreCaseReviewData,
} from "@/data/repository";
import { formatCaseDate } from "@/data/viewModels";
import { cn } from "@/lib/utils";

/* -------------------------- helpers -------------------------- */

const personName = (id) => {
  if (!id) return "—";
  const p = getPerson(id);
  return p ? p.fullName : id;
};

const shortSex = (s) => (s === "Female" ? "F" : s === "Male" ? "M" : (s || "").slice(0, 1));

const shortDiagnosis = (c) => {
  const bits = [];
  if (c.laterality) bits.push(c.laterality);
  bits.push(c.diagnosis);
  if (c.tumorSizeCm) bits.push(`(${c.tumorSizeCm} cm)`);
  return bits.join(" ");
};

const statusTone = (s) => {
  const t = String(s || "").toLowerCase();
  if (t.includes("recover") || t.includes("stable")) return "ok";
  if (t.includes("post") || t.includes("intra") || t.includes("scheduled") || t.includes("pre")) return "amber";
  if (t.includes("closed") || t.includes("archived")) return "plain";
  return "blue";
};

const eventCategoryTone = (cat) => {
  const c = String(cat || "").toLowerCase();
  if (c.includes("complication") || c.includes("event") || c.includes("intraop")) return "amber";
  if (c.includes("imaging")) return "blue";
  if (c.includes("m&m") || c.includes("mm")) return "warn";
  if (c.includes("reflection") || c.includes("pearl")) return "amber";
  return "plain";
};

const eventIcon = (cat) => {
  const c = String(cat || "").toLowerCase();
  if (c.includes("imaging")) return ImageIcon;
  if (c.includes("m&m") || c.includes("mm")) return AlertOctagon;
  if (c.includes("reflection") || c.includes("pearl")) return Lightbulb;
  if (c.includes("operat") || c.includes("intraop")) return Scissors;
  if (c.includes("clinic") || c.includes("encounter") || c.includes("follow")) return Stethoscope;
  return Waves;
};

/* -------------------------- page -------------------------- */

export default function Cases() {
  const [tab, setTab] = useState("operative"); // "operative" | "clinics"
  const [query, setQuery] = useState("");
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [fullCaseOpen, setFullCaseOpen] = useState(false);

  const allCases = useMemo(() => getCases(), []);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allCases;
    return allCases.filter((c) =>
      [
        c.caseId,
        c.patientLabel,
        c.diagnosis,
        c.pathologySubtype,
        c.approach,
        c.currentStatus,
        c.laterality,
      ]
        .filter(Boolean)
        .some((f) => String(f).toLowerCase().includes(q))
    );
  }, [allCases, query]);

  const selectedCase = useMemo(
    () => (selectedCaseId ? allCases.find((c) => c.caseId === selectedCaseId) : null),
    [selectedCaseId, allCases]
  );

  const handleSelect = (id) => {
    setSelectedCaseId(id);
    setMemoryOpen(false);
  };

  const openMemory = (id) => {
    setSelectedCaseId(id);
    setMemoryOpen(true);
  };

  return (
    <div className="flex flex-col gap-6" data-testid="cases-module">
      {/* Header */}
      <section className="nn-enter flex items-end justify-between gap-8 pt-2">
        <div>
          <div className="nn-eyebrow">NeuroNeeti · Module</div>
          <h1 className="mt-3 flex items-center gap-3 nn-h1 text-[hsl(var(--nn-ink))]">
            <span className="grid h-10 w-10 place-items-center rounded-xl border border-white/[0.08] bg-white/[0.04] text-[hsl(var(--nn-blue))]">
              <FolderKanban className="h-4 w-4" strokeWidth={1.6} />
            </span>
            Cases
          </h1>
          <p className="mt-2 text-[14px] leading-[22px] text-[hsl(var(--nn-ink-muted))]">
            Longitudinal case ledger · {allCases.length} cases across every stage of care
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="nn-chip nn-chip-blue">Workbook · live</span>
          <span className="nn-chip">Synthetic dataset</span>
        </div>
      </section>

      {/* Tabs + Search */}
      <section className="nn-enter nn-enter-1 flex items-center justify-between gap-4">
        <div
          className="inline-flex items-center rounded-2xl border border-white/[0.08] bg-white/[0.03] p-1"
          role="tablist"
          data-testid="cases-tabs"
        >
          <TabButton
            active={tab === "operative"}
            onClick={() => setTab("operative")}
            Icon={Scissors}
            label="Operative"
            testId="cases-tab-operative"
          />
          <TabButton
            active={tab === "clinics"}
            onClick={() => setTab("clinics")}
            Icon={Stethoscope}
            label="Clinics"
            testId="cases-tab-clinics"
          />
        </div>

        <div className="flex flex-1 max-w-md items-center gap-2 rounded-2xl border border-white/[0.08] bg-white/[0.03] px-3 py-2">
          <Search className="h-3.5 w-3.5 text-[hsl(var(--nn-ink-muted))]" strokeWidth={1.8} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search patient, diagnosis, approach…"
            className="w-full bg-transparent text-[12.5px] text-[hsl(var(--nn-ink))] placeholder:text-[hsl(var(--nn-ink-faint))] outline-none"
            data-testid="cases-search"
          />
          <span className="text-[10.5px] font-mono text-[hsl(var(--nn-ink-faint))]">
            {filtered.length}/{allCases.length}
          </span>
        </div>
      </section>

      {/* Split view: list (top) + optional memory panel (bottom) */}
      <section
        className="nn-enter nn-enter-2 flex flex-col gap-5"
        style={{
          minHeight: "calc(100vh - 320px)",
        }}
      >
        {/* Case List */}
        <GlassCard
          className={cn(
            "nn-lift !p-0 overflow-hidden transition-all duration-500",
            memoryOpen ? "max-h-[38vh]" : "max-h-none"
          )}
        >
          <div
            className={cn(
              "nn-scroll overflow-y-auto",
              memoryOpen ? "max-h-[38vh]" : "max-h-[70vh]"
            )}
          >
            <table className="w-full text-left" data-testid="cases-table">
              <thead className="sticky top-0 z-10 bg-[rgba(18,26,44,0.9)] backdrop-blur">
                <tr className="border-b border-white/[0.06] text-[10.5px] uppercase tracking-[0.12em] text-[hsl(var(--nn-ink-faint))]">
                  <th className="px-5 py-3 font-medium">Case</th>
                  <th className="px-3 py-3 font-medium">Patient</th>
                  <th className="px-3 py-3 font-medium">Diagnosis</th>
                  {tab === "operative" ? (
                    <>
                      <th className="px-3 py-3 font-medium">Approach · Resection</th>
                      <th className="px-3 py-3 font-medium">Attending · Resident</th>
                      <th className="px-3 py-3 font-medium">Complication · M&M</th>
                      <th className="px-3 py-3 font-medium">Date of surgery</th>
                    </>
                  ) : (
                    <>
                      <th className="px-3 py-3 font-medium">Latest encounter</th>
                      <th className="px-3 py-3 font-medium">HB · Hearing</th>
                      <th className="px-3 py-3 font-medium">Current status</th>
                      <th className="px-3 py-3 font-medium">Last seen</th>
                    </>
                  )}
                  <th className="px-5 py-3 text-right font-medium">Memory</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <CaseRow
                    key={c.caseId}
                    c={c}
                    tab={tab}
                    selected={selectedCaseId === c.caseId}
                    onSelect={() => handleSelect(c.caseId)}
                    onOpenMemory={() => openMemory(c.caseId)}
                    onOpenFull={() => {
                      setSelectedCaseId(c.caseId);
                      setFullCaseOpen(true);
                    }}
                    compact={memoryOpen}
                  />
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-5 py-12 text-center text-[12px] text-[hsl(var(--nn-ink-muted))]">
                      No cases match "{query}".
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </GlassCard>

        {/* Surgical Memory panel */}
        {memoryOpen && selectedCase && (
          <SurgicalMemoryPanel
            caseRecord={selectedCase}
            onClose={() => setMemoryOpen(false)}
            onOpenFull={() => setFullCaseOpen(true)}
          />
        )}
      </section>

      {/* Full case modal */}
      {fullCaseOpen && selectedCase && (
        <FullCaseModal
          caseRecord={selectedCase}
          onClose={() => setFullCaseOpen(false)}
        />
      )}
    </div>
  );
}

/* -------------------------- tab button -------------------------- */

function TabButton({ active, onClick, Icon, label, testId }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testId}
      className={cn(
        "group inline-flex items-center gap-1.5 rounded-xl px-3.5 py-1.5 text-[12px] font-medium transition-all duration-200",
        active
          ? "bg-white/[0.09] text-[hsl(var(--nn-ink))] shadow-[inset_0_1px_0_rgba(255,255,255,0.10)]"
          : "text-[hsl(var(--nn-ink-muted))] hover:text-[hsl(var(--nn-ink))]"
      )}
    >
      <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
      {label}
    </button>
  );
}

/* -------------------------- case row -------------------------- */

function CaseRow({ c, tab, selected, onSelect, onOpenMemory, onOpenFull, compact }) {
  const encounters = useMemo(() => getEncountersByCaseId(c.caseId), [c.caseId]);
  const latest = encounters.length ? encounters[encounters.length - 1] : null;
  const reflCount = useMemo(() => getReflectionsByCaseId(c.caseId).length, [c.caseId]);
  const timelineCount = useMemo(() => getTimelineByCaseId(c.caseId).length, [c.caseId]);
  const mmCount = useMemo(() => getMMByCaseId(c.caseId).length, [c.caseId]);

  return (
    <tr
      onClick={onSelect}
      data-testid={`case-row-${c.caseId}`}
      data-selected={selected || undefined}
      className={cn(
        "group cursor-pointer border-b border-white/[0.04] transition-all duration-200 hover:bg-white/[0.035]",
        selected && "bg-[rgba(96,165,250,0.06)] hover:bg-[rgba(96,165,250,0.08)]"
      )}
    >
      {/* Case ID */}
      <td className="px-5 py-3 align-top">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "h-1.5 w-1.5 shrink-0 rounded-full transition",
              selected ? "bg-[hsl(var(--nn-blue))]" : "bg-white/[0.15] group-hover:bg-white/[0.3]"
            )}
          />
          <div className="font-mono text-[11px] font-medium text-[hsl(var(--nn-ink))]">
            {c.caseId}
          </div>
        </div>
        <div className="mt-0.5 pl-3.5 text-[10.5px] text-[hsl(var(--nn-ink-faint))]">
          Koos {c.koosGrade || "—"}
        </div>
      </td>

      {/* Patient */}
      <td className="px-3 py-3 align-top">
        <div className="text-[12.5px] font-medium text-[hsl(var(--nn-ink))]">
          {c.patientLabel} · {c.age}
          {shortSex(c.sex)}
        </div>
        <div className="text-[10.5px] font-mono text-[hsl(var(--nn-ink-faint))]">
          {c.syntheticPatientId}
        </div>
      </td>

      {/* Diagnosis */}
      <td className="px-3 py-3 align-top">
        <div className="text-[12px] text-[hsl(var(--nn-ink))]">{shortDiagnosis(c)}</div>
        <div className="mt-0.5 text-[10.5px] text-[hsl(var(--nn-ink-muted))]">
          {c.pathologySubtype}
        </div>
      </td>

      {tab === "operative" ? (
        <>
          <td className="px-3 py-3 align-top">
            <div className="text-[12px] text-[hsl(var(--nn-ink))]">{c.approach || "—"}</div>
            <div className="mt-0.5 text-[10.5px] text-[hsl(var(--nn-ink-muted))]">
              {c.extentOfResection || "—"}
            </div>
          </td>
          <td className="px-3 py-3 align-top">
            <div className="text-[12px] text-[hsl(var(--nn-ink))]">
              {personName(c.primaryAttendingId)}
            </div>
            <div className="mt-0.5 text-[10.5px] text-[hsl(var(--nn-ink-muted))]">
              {personName(c.primaryResidentId)}
            </div>
          </td>
          <td className="px-3 py-3 align-top">
            <div className="text-[12px] text-[hsl(var(--nn-ink))]">
              {c.otherComplication && c.otherComplication !== "None" ? c.otherComplication : "None"}
            </div>
            <div className="mt-0.5">
              <MMBadge status={c.mAndMStatus} />
            </div>
          </td>
          <td className="px-3 py-3 align-top">
            <div className="font-mono text-[11px] text-[hsl(var(--nn-ink))]">
              {formatCaseDate(c.dateOfSurgery)}
            </div>
          </td>
        </>
      ) : (
        <>
          <td className="px-3 py-3 align-top">
            <div className="text-[12px] text-[hsl(var(--nn-ink))]">
              {latest ? latest.encounterType : "—"}
            </div>
            <div className="mt-0.5 text-[10.5px] text-[hsl(var(--nn-ink-muted))]">
              {latest ? latest.relativeTiming : ""}
            </div>
          </td>
          <td className="px-3 py-3 align-top">
            <div className="text-[12px] text-[hsl(var(--nn-ink))]">
              {c.sixMonthHbGrade || c.threeWeekHbGrade || c.pod1HbGrade || "—"}
            </div>
            <div className="mt-0.5 text-[10.5px] text-[hsl(var(--nn-ink-muted))]">
              {c.hearingStatusPostop || c.hearingStatusPreop || ""}
            </div>
          </td>
          <td className="px-3 py-3 align-top">
            <StatusChip status={c.currentStatus} />
          </td>
          <td className="px-3 py-3 align-top">
            <div className="font-mono text-[11px] text-[hsl(var(--nn-ink))]">
              {latest ? formatCaseDate(latest.encounterDate) : "—"}
            </div>
          </td>
        </>
      )}

      {/* Memory density + actions */}
      <td className="px-5 py-3 text-right align-top">
        <div className="flex items-center justify-end gap-3">
          <div className="text-right">
            <div className="text-[10.5px] font-mono text-[hsl(var(--nn-ink-muted))]">
              {timelineCount} tl · {reflCount} refl · {mmCount} m&m
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenMemory();
            }}
            className="nn-btn nn-btn-primary"
            data-testid={`case-open-memory-${c.caseId}`}
          >
            <Layers className="h-3 w-3" strokeWidth={1.8} />
            {compact && selected ? "In view" : "Memory"}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenFull();
            }}
            className="nn-btn"
            aria-label="Open full case"
            data-testid={`case-open-full-${c.caseId}`}
          >
            Full
            <ArrowUpRight className="h-3 w-3" strokeWidth={1.8} />
          </button>
        </div>
      </td>
    </tr>
  );
}

function MMBadge({ status }) {
  if (!status) return <span className="text-[10.5px] text-[hsl(var(--nn-ink-faint))]">—</span>;
  const s = String(status).toLowerCase();
  const cls =
    s.includes("closed") || s.includes("reviewed")
      ? "nn-chip"
      : s.includes("pending") || s.includes("scheduled")
        ? "nn-chip-warn"
        : s.includes("not")
          ? "nn-chip"
          : "nn-chip-blue";
  return <span className={cn("nn-chip", cls)} style={{ fontSize: 10 }}>{status}</span>;
}

function StatusChip({ status }) {
  const tone = statusTone(status);
  const cls =
    tone === "ok"
      ? "nn-chip-ok"
      : tone === "amber"
        ? "nn-chip-amber"
        : tone === "blue"
          ? "nn-chip-blue"
          : "nn-chip";
  return <span className={cn("nn-chip", cls)} style={{ fontSize: 10.5 }}>{status || "—"}</span>;
}

/* -------------------------- surgical memory panel -------------------------- */

function SurgicalMemoryPanel({ caseRecord, onClose, onOpenFull }) {
  const timeline = useMemo(() => getTimelineByCaseId(caseRecord.caseId), [caseRecord.caseId]);
  const [activeIdx, setActiveIdx] = useState(0);
  const scrollerRef = useRef(null);

  // Reset selected node when case changes
  useEffect(() => {
    setActiveIdx(0);
    if (scrollerRef.current) scrollerRef.current.scrollLeft = 0;
  }, [caseRecord.caseId]);

  const active = timeline[activeIdx] || null;

  const scrollBy = (delta) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  return (
    <GlassCard
      className="nn-enter nn-enter-1 nn-lift !p-0 overflow-hidden"
      testId="surgical-memory-panel"
    >
      {/* Header */}
      <div
        className="flex items-center justify-between gap-4 px-6 py-4"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
      >
        <div className="min-w-0">
          <div className="nn-eyebrow">Surgical memory · timeline</div>
          <div className="mt-1 flex items-baseline gap-3">
            <span className="text-[15px] font-semibold tracking-tight text-[hsl(var(--nn-ink))]">
              {caseRecord.patientLabel} · {shortDiagnosis(caseRecord)}
            </span>
            <span className="font-mono text-[11px] text-[hsl(var(--nn-ink-faint))]">
              {caseRecord.caseId}
            </span>
          </div>
          <div className="mt-0.5 text-[11.5px] text-[hsl(var(--nn-ink-muted))]">
            {timeline.length} events · {caseRecord.approach || "—"} ·{" "}
            {caseRecord.currentStatus || "—"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onOpenFull}
            className="nn-btn"
            data-testid="memory-open-full"
          >
            Open full case
            <ArrowUpRight className="h-3 w-3" strokeWidth={1.8} />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="nn-icon-btn"
            aria-label="Close surgical memory"
            data-testid="memory-close"
          >
            <X className="h-4 w-4" strokeWidth={1.8} />
          </button>
        </div>
      </div>

      {/* Horizontal timeline */}
      <div className="relative px-6 py-6">
        <div className="pointer-events-none absolute left-6 right-6 top-1/2 h-px -translate-y-1/2 bg-white/[0.08]" />

        {/* Scroll controls */}
        <button
          type="button"
          onClick={() => scrollBy(-360)}
          className="absolute left-2 top-1/2 z-20 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full border border-white/[0.10] bg-[rgba(20,28,46,0.8)] text-[hsl(var(--nn-ink-muted))] backdrop-blur transition hover:text-[hsl(var(--nn-ink))]"
          aria-label="Scroll timeline left"
          data-testid="timeline-scroll-left"
        >
          <ChevronLeft className="h-3.5 w-3.5" strokeWidth={1.8} />
        </button>
        <button
          type="button"
          onClick={() => scrollBy(360)}
          className="absolute right-2 top-1/2 z-20 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full border border-white/[0.10] bg-[rgba(20,28,46,0.8)] text-[hsl(var(--nn-ink-muted))] backdrop-blur transition hover:text-[hsl(var(--nn-ink))]"
          aria-label="Scroll timeline right"
          data-testid="timeline-scroll-right"
        >
          <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.8} />
        </button>

        <div
          ref={scrollerRef}
          className="nn-scroll relative flex items-stretch gap-3 overflow-x-auto pb-2 pl-8 pr-8"
          data-testid="timeline-scroller"
        >
          {timeline.map((ev, i) => (
            <TimelineNode
              key={ev.timelineEventId}
              event={ev}
              index={i}
              total={timeline.length}
              active={i === activeIdx}
              onClick={() => setActiveIdx(i)}
            />
          ))}
          {timeline.length === 0 && (
            <div className="w-full py-10 text-center text-[12px] text-[hsl(var(--nn-ink-muted))]">
              No timeline events captured for this case yet.
            </div>
          )}
        </div>
      </div>

      {/* Detail panel below timeline */}
      {active && (
        <TimelineDetail event={active} caseRecord={caseRecord} />
      )}
    </GlassCard>
  );
}

function TimelineNode({ event, index, total, active, onClick }) {
  const Icon = eventIcon(event.eventCategory);
  const tone = eventCategoryTone(event.eventCategory);
  const dotColor =
    tone === "amber"
      ? "hsl(var(--nn-amber))"
      : tone === "blue"
        ? "hsl(var(--nn-blue))"
        : tone === "warn"
          ? "hsl(var(--nn-warn))"
          : "hsl(var(--nn-ok))";
  const highlight = String(event.highlightLevel || "").toLowerCase().includes("high");
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`timeline-node-${event.timelineEventId}`}
      data-active={active || undefined}
      className={cn(
        "group relative flex w-[220px] shrink-0 flex-col rounded-2xl border px-3 py-3 text-left transition-all duration-300",
        active
          ? "border-white/[0.20] bg-white/[0.09] shadow-[0_10px_28px_-14px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.08)]"
          : "border-white/[0.06] bg-white/[0.03] hover:-translate-y-0.5 hover:border-white/[0.14] hover:bg-white/[0.055]"
      )}
    >
      {/* Node dot on the axis */}
      <span
        className="absolute -top-[6px] left-4 h-3 w-3 rounded-full border border-white/20"
        style={{
          background: dotColor,
          boxShadow: active
            ? `0 0 12px ${dotColor}`
            : highlight
              ? `0 0 6px ${dotColor}`
              : "none",
        }}
      />
      <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">
        <Icon className="h-2.5 w-2.5" strokeWidth={1.8} />
        {event.eventCategory}
      </div>
      <div className="mt-1 font-mono text-[10.5px] text-[hsl(var(--nn-ink-muted))]">
        {formatCaseDate(event.eventDate)} · {event.relativeTiming}
      </div>
      <div className="mt-1.5 line-clamp-2 text-[12.5px] font-medium leading-[17px] text-[hsl(var(--nn-ink))]">
        {event.eventTitle}
      </div>
      <div className="mt-0.5 line-clamp-2 text-[11px] leading-[15px] text-[hsl(var(--nn-ink-muted))]">
        {event.eventSummary}
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px] font-mono text-[hsl(var(--nn-ink-faint))]">
        <span>
          {index + 1}/{total}
        </span>
        <span>{event.timelineEventId}</span>
      </div>
    </button>
  );
}

function TimelineDetail({ event, caseRecord }) {
  const linkedReflections = useMemo(() => {
    const all = getReflectionsByCaseId(caseRecord.caseId);
    return all.filter(
      (r) =>
        r.encounterId === event.encounterId ||
        r.reflectionId === event.sourceId ||
        String(event.sourceType).toLowerCase() === "reflection"
    );
  }, [caseRecord.caseId, event.encounterId, event.sourceId, event.sourceType]);

  const linkedPearls = useMemo(() => {
    const pearls = getOperativePearlsByCaseId(caseRecord.caseId);
    // pearl is linked if its operativePhase overlaps with event title/summary, or shares a reflection
    return pearls.filter((p) =>
      linkedReflections.find((r) => r.operativePearlId === p.operativePearlId)
    );
  }, [caseRecord.caseId, linkedReflections]);

  const linkedImaging = useMemo(() => {
    const imgs = getImagingByCaseId(caseRecord.caseId);
    return imgs.filter((i) => i.encounterId === event.encounterId);
  }, [caseRecord.caseId, event.encounterId]);

  const Icon = eventIcon(event.eventCategory);

  return (
    <div
      className="grid grid-cols-3 gap-5 px-6 py-6"
      style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
      data-testid="timeline-detail"
    >
      {/* Primary event card */}
      <div className="col-span-2 nn-glass-inset p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">
              <Icon className="h-3 w-3" strokeWidth={1.8} />
              {event.eventCategory}
              <span>·</span>
              <span className="font-mono normal-case tracking-normal">
                {event.timelineEventId}
              </span>
            </div>
            <h4 className="mt-1.5 text-[15px] font-semibold tracking-tight text-[hsl(var(--nn-ink))]">
              {event.eventTitle}
            </h4>
            <div className="mt-0.5 flex items-center gap-3 text-[11px] font-mono text-[hsl(var(--nn-ink-muted))]">
              <span className="flex items-center gap-1">
                <CalendarDays className="h-3 w-3" strokeWidth={1.8} />
                {formatCaseDate(event.eventDate)}
              </span>
              <span>·</span>
              <span>{event.relativeTiming}</span>
              {event.createdByPersonId && (
                <>
                  <span>·</span>
                  <span className="flex items-center gap-1">
                    <User className="h-3 w-3" strokeWidth={1.8} />
                    {personName(event.createdByPersonId)}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <ProvenanceTag label="Case" ref={caseRecord.caseId} />
            {event.encounterId && (
              <ProvenanceTag label="Encounter" ref={event.encounterId} />
            )}
          </div>
        </div>

        <p className="mt-4 text-[13px] leading-[20px] text-[hsl(var(--nn-ink))]">
          {event.eventSummary}
        </p>
        {event.outcome && (
          <div className="mt-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
            <div className="nn-eyebrow" style={{ color: "hsl(var(--nn-amber))" }}>
              Outcome
            </div>
            <p className="mt-1 text-[12.5px] leading-[19px] text-[hsl(var(--nn-ink))]">
              {event.outcome}
            </p>
          </div>
        )}

        {/* Linked reflections */}
        {linkedReflections.length > 0 && (
          <div className="mt-4">
            <SectionLabel Icon={Lightbulb} label="Reflections" count={linkedReflections.length} />
            <ul className="mt-2 flex flex-col gap-2">
              {linkedReflections.slice(0, 3).map((r) => (
                <li
                  key={r.reflectionId}
                  className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3"
                >
                  <div className="flex items-center gap-2 text-[10.5px] font-mono text-[hsl(var(--nn-ink-faint))]">
                    <span>{r.reflectionType}</span>
                    <span>·</span>
                    <span>{r.reflectionId}</span>
                    <span className="ml-auto">{r.dateCaptured?.slice(0, 10)}</span>
                  </div>
                  <p className="mt-1 text-[12px] italic leading-[18px] text-[hsl(var(--nn-ink))]">
                    {r.reflectionText}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Side rail: Pearls + Imaging */}
      <div className="flex flex-col gap-4">
        {linkedPearls.length > 0 && (
          <div className="nn-glass-inset p-4">
            <SectionLabel Icon={Lightbulb} label="Operative pearls" count={linkedPearls.length} />
            <ul className="mt-2 flex flex-col gap-2">
              {linkedPearls.slice(0, 2).map((p) => (
                <li
                  key={p.operativePearlId}
                  className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5"
                >
                  <div className="text-[10.5px] font-mono text-[hsl(var(--nn-ink-faint))]">
                    {p.operativePhase}
                  </div>
                  <p className="mt-1 text-[12px] leading-[17px] text-[hsl(var(--nn-ink))]">
                    {p.operativePearl}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}

        {linkedImaging.length > 0 && (
          <div className="nn-glass-inset p-4">
            <SectionLabel Icon={ImageIcon} label="Imaging" count={linkedImaging.length} />
            <ul className="mt-2 flex flex-col gap-2">
              {linkedImaging.slice(0, 3).map((img) => (
                <li
                  key={img.imagingId}
                  className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5"
                >
                  <div className="flex items-center justify-between text-[10.5px] font-mono text-[hsl(var(--nn-ink-faint))]">
                    <span>
                      {img.modality} · {img.sequence}
                    </span>
                    <span>{img.imagingId}</span>
                  </div>
                  <p className="mt-1 text-[11.5px] leading-[16px] text-[hsl(var(--nn-ink))]">
                    {img.keyFinding}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}

        {linkedPearls.length === 0 && linkedImaging.length === 0 && (
          <div className="nn-glass-inset p-4 text-[11.5px] leading-[17px] text-[hsl(var(--nn-ink-muted))]">
            No pearls or imaging directly linked to this event yet. Select adjacent nodes to see
            operative reflections and imaging captured elsewhere in the case.
          </div>
        )}

        {/* Case snapshot */}
        <div className="nn-glass-inset p-4">
          <SectionLabel Icon={Activity} label="Case snapshot" />
          <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2 text-[11px]">
            <Snap label="Approach" value={caseRecord.approach} />
            <Snap label="Resection" value={caseRecord.extentOfResection} />
            <Snap label="Koos" value={caseRecord.koosGrade} />
            <Snap label="POD-1 HB" value={caseRecord.pod1HbGrade} />
            <Snap label="3-wk HB" value={caseRecord.threeWeekHbGrade} />
            <Snap label="6-mo HB" value={caseRecord.sixMonthHbGrade} />
          </dl>
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ Icon, label, count }) {
  return (
    <div className="flex items-center gap-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">
      <Icon className="h-2.5 w-2.5" strokeWidth={1.8} />
      {label}
      {count != null && (
        <span className="ml-1 rounded-full border border-white/[0.08] bg-white/[0.04] px-1.5 font-mono normal-case tracking-normal text-[10px] text-[hsl(var(--nn-ink-muted))]">
          {count}
        </span>
      )}
    </div>
  );
}

function Snap({ label, value }) {
  return (
    <>
      <dt className="text-[hsl(var(--nn-ink-faint))]">{label}</dt>
      <dd className="text-right text-[hsl(var(--nn-ink))]">{value || "—"}</dd>
    </>
  );
}

/* -------------------------- full case modal -------------------------- */

function FullCaseModal({ caseRecord, onClose }) {
  const bundle = useMemo(() => getPreCaseReviewData(caseRecord.caseId), [caseRecord.caseId]);
  const surg = bundle?.surgicalDetail || getSurgicalDetailByCaseId(caseRecord.caseId);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!bundle) return null;

  return (
    <>
      <div className="nn-source-scrim" onClick={onClose} data-testid="full-case-scrim" />
      <div
        className="nn-source-panel"
        role="dialog"
        aria-label={`Full case · ${caseRecord.caseId}`}
        data-testid="full-case-panel"
        style={{ width: "min(880px, calc(100% - 48px))" }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between gap-3 px-6 py-4"
          style={{
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            background: "linear-gradient(180deg, rgba(255,255,255,0.04), transparent)",
          }}
        >
          <div className="flex min-w-0 items-center gap-3">
            <span
              className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-white/[0.10] text-[hsl(var(--nn-blue))]"
              style={{
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))",
              }}
            >
              <FolderKanban className="h-4 w-4" strokeWidth={1.8} />
            </span>
            <div className="min-w-0 leading-tight">
              <div className="flex items-center gap-2">
                <span className="nn-eyebrow">Full case</span>
                <span className="font-mono text-[10.5px] text-[hsl(var(--nn-ink-faint))]">
                  {caseRecord.caseId}
                </span>
              </div>
              <div className="truncate text-[14px] font-semibold tracking-tight text-white">
                {caseRecord.patientLabel} · {shortDiagnosis(caseRecord)}
              </div>
              <div className="truncate text-[11.5px] text-[hsl(var(--nn-ink-muted))]">
                {caseRecord.approach || "—"} · {caseRecord.currentStatus || "—"}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="nn-icon-btn"
            aria-label="Close full case"
            data-testid="full-case-close"
          >
            <X className="h-4 w-4" strokeWidth={1.8} />
          </button>
        </div>

        {/* Body */}
        <div className="nn-source-body">
          {/* Header cards */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <HeaderStat label="Age · Sex" value={`${caseRecord.age} · ${caseRecord.sex}`} />
            <HeaderStat label="Koos" value={caseRecord.koosGrade} />
            <HeaderStat label="Attending" value={personName(caseRecord.primaryAttendingId)} />
            <HeaderStat label="Resident" value={personName(caseRecord.primaryResidentId)} />
            <HeaderStat label="Date of surgery" value={formatCaseDate(caseRecord.dateOfSurgery)} />
            <HeaderStat label="Approach" value={caseRecord.approach} />
            <HeaderStat label="Resection" value={caseRecord.extentOfResection} />
            <HeaderStat label="Current status" value={caseRecord.currentStatus} />
          </div>

          {/* Operative summary */}
          {surg && (
            <div className="mt-5 nn-glass-inset p-4">
              <SectionLabel Icon={Scissors} label="Operative summary" />
              <p className="mt-2 text-[12.5px] leading-[19px] text-[hsl(var(--nn-ink))]">
                {surg.technicalSummary || "—"}
              </p>
              {surg.immediateOperativeLearningPoint && (
                <div className="mt-2 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
                  <div className="nn-eyebrow" style={{ color: "hsl(var(--nn-amber))" }}>
                    Immediate learning point
                  </div>
                  <p className="mt-1 text-[12px] leading-[18px] text-[hsl(var(--nn-ink))]">
                    {surg.immediateOperativeLearningPoint}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Encounters */}
          {bundle.encounters?.length > 0 && (
            <div className="mt-5">
              <SectionLabel
                Icon={Stethoscope}
                label="Clinical encounters"
                count={bundle.encounters.length}
              />
              <ul className="mt-2 flex flex-col gap-2">
                {bundle.encounters.map((e) => (
                  <li
                    key={e.encounterId}
                    className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-3"
                  >
                    <div className="flex items-center justify-between gap-2 text-[11px]">
                      <span className="font-medium text-[hsl(var(--nn-ink))]">
                        {e.encounterType}
                      </span>
                      <span className="font-mono text-[10.5px] text-[hsl(var(--nn-ink-faint))]">
                        {formatCaseDate(e.encounterDate)} · {e.relativeTiming}
                      </span>
                    </div>
                    {e.chiefConcern && (
                      <div className="mt-1 text-[11.5px] text-[hsl(var(--nn-ink-muted))]">
                        {e.chiefConcern}
                      </div>
                    )}
                    {e.assessment && (
                      <div className="mt-1 text-[11.5px] text-[hsl(var(--nn-ink))]">
                        <span className="text-[hsl(var(--nn-ink-faint))]">Assessment · </span>
                        {e.assessment}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Reflections */}
          {bundle.reflections?.length > 0 && (
            <div className="mt-5">
              <SectionLabel
                Icon={Lightbulb}
                label="Reflections"
                count={bundle.reflections.length}
              />
              <ul className="mt-2 flex flex-col gap-2">
                {bundle.reflections.slice(0, 6).map((r) => (
                  <li
                    key={r.reflectionId}
                    className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-3"
                  >
                    <div className="flex items-center gap-2 text-[10.5px] font-mono text-[hsl(var(--nn-ink-faint))]">
                      <span>{r.reflectionType}</span>
                      <span>·</span>
                      <span>{r.reflectionId}</span>
                      <span className="ml-auto">
                        {r.dateCaptured?.slice(0, 10) || ""}
                      </span>
                    </div>
                    <p className="mt-1 text-[12px] italic leading-[18px] text-[hsl(var(--nn-ink))]">
                      {r.reflectionText}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Imaging */}
          {bundle.imaging?.length > 0 && (
            <div className="mt-5">
              <SectionLabel Icon={ImageIcon} label="Imaging" count={bundle.imaging.length} />
              <ul className="mt-2 flex flex-col gap-2">
                {bundle.imaging.map((img) => (
                  <li
                    key={img.imagingId}
                    className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-3"
                  >
                    <div className="flex items-center justify-between gap-2 text-[10.5px] font-mono text-[hsl(var(--nn-ink-faint))]">
                      <span>
                        {img.modality} · {img.sequence}
                      </span>
                      <span>{formatCaseDate(img.studyDate)}</span>
                    </div>
                    <div className="mt-1 text-[12px] text-[hsl(var(--nn-ink))]">
                      {img.keyFinding}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* M&M */}
          {bundle.mmRecords?.length > 0 && (
            <div className="mt-5">
              <SectionLabel
                Icon={AlertOctagon}
                label="Morbidity & mortality"
                count={bundle.mmRecords.length}
              />
              <ul className="mt-2 flex flex-col gap-2">
                {bundle.mmRecords.map((m) => (
                  <li
                    key={m.mAndMId}
                    className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-3"
                  >
                    <div className="flex items-center justify-between gap-2 text-[10.5px] font-mono text-[hsl(var(--nn-ink-faint))]">
                      <span>{m.mAndMId}</span>
                      <span>{formatCaseDate(m.meetingDate)}</span>
                    </div>
                    <div className="mt-1 text-[12px] text-[hsl(var(--nn-ink))]">
                      {m.complication}
                    </div>
                    {m.learningPoints && (
                      <div className="mt-1 text-[11.5px] text-[hsl(var(--nn-ink-muted))]">
                        <span className="text-[hsl(var(--nn-ink-faint))]">Learning · </span>
                        {m.learningPoints}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function HeaderStat({ label, value }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-2.5">
      <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">
        {label}
      </div>
      <div className="mt-1 text-[12.5px] text-[hsl(var(--nn-ink))]">{value || "—"}</div>
    </div>
  );
}
